// ProductsView.jsx
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../AuthContext";
import { API_URL } from "../config/api";
import styles from "../styles/productsView.module.css";
import CustomSelect from "../customComponents/CustomSelect";
import FilterSidebar from "../customComponents/FilterSidebar";
import CategoriesSection from "./tables/CategoriesSection.jsx";
import RefundSection from "./tables/RefundSection.jsx";
import ArrivalSection from "./tables/ArrivalSection";
import MovementSection from "./tables/MovementSection.jsx";
import WriteoffSection from "./tables/WriteoffSection.jsx";
import RevisionSection from "./tables/RevisionSection.jsx";
import ActionBar from "../customComponents/ActionBar";
import ProductDrawer from "../customComponents/ProductDrawer";
import AddSingleProductDrawer from "../customComponents/AddSingleProductDrawer";
import CustomDeleteModal from "../customComponents/CustomDeleteModal";
import ArrivalModal from "../customComponents/ArrivalModal";
import BarcodePrintModal from "../customComponents/BarcodePrintModal";
import MovementModal from "../customComponents/MovementModal";
import CategoryModal from "../customComponents/CategoryModal";
import AddMultiProductDrawer from "../customComponents/AddMultiProductDrawer";

const sections = [
  { id: "nomenclature", label: "Номенклатура" },
  { id: "categories", label: "Категории" },
  { id: "arrival", label: "Приемка" },
  { id: "movement", label: "Перемещение" },
  { id: "writeoff", label: "Списания" },
  { id: "return", label: "Возврат поставщику" },
  { id: "revision", label: "Ревизия" },
];

// ✅ типы для селекта (label показываем пользователю, value отправляем в API)
const PRODUCT_TYPES = [
  { value: "simple", label: "Товар однотипный" },
  { value: "variable", label: "Товар многотипный" },
  { value: "composite", label: "Составной товар" },
  { value: "service", label: "Услуга" },
  { value: "universal", label: "Универсальный товар" },
];

const ProductsView = (props) => {
  const { token } = useAuth();
  const [productToEdit, setProductToEdit] = useState(null);
  const { setShowMenuTwo } = props;

  const [activeSection, setActiveSection] = useState("nomenclature");
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);

  // ✅ dropdown state
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    placement: "bottom",
  });
  const dropdownMenuRef = useRef(null);
  const dotsBtnRefs = useRef(new Map()); // id -> button element

  const [productToView, setProductToView] = useState(null);
  const [showAddSingleProduct, setShowAddSingleProduct] = useState(false);
  const [productToPrint, setProductToPrint] = useState(null);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [productToMove, setProductToMove] = useState(null);
  const [showAddMultiProduct, setShowAddMultiProduct] = useState(false);
  const [categoriesList, setCategoriesList] = useState([]);
  const [storagesList, setStoragesList] = useState([]);
  const [selectedStorage, setSelectedStorage] = useState(null);

  const [tableData, setTableData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [singleDeleteItem, setSingleDeleteItem] = useState(null);
  const [showArrivalModal, setShowArrivalModal] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [type, setType] = useState("");
  const [searchItem, setSearchItem] = useState("");
  const [category, setCategory] = useState("");

  const rowsPerPage = 50;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const [filterParams, setFilterParams] = useState({});

  const authFetch = async (url, options = {}) => {
    const tk = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${tk}`,
      "Content-Type": "application/json",
      ...options.headers,
    };
    const res = await fetch(url, { ...options, headers });
    return res.json();
  };

  // ===== DROPDOWN LOGIC (portal + flip + close) =====
  const handleOptionsClick = useCallback((id) => {
    setOpenDropdownId((prev) => {
      const next = prev === id ? null : id;
      if (!next) return null;

      const btnEl = dotsBtnRefs.current.get(id);
      if (!btnEl) return next;

      const rect = btnEl.getBoundingClientRect();

      // временно ставим "вниз", после рендера меню useLayoutEffect сделает flip и clamp
      setDropdownPos((p) => ({
        ...p,
        top: rect.bottom + 6,
        left: rect.right, // потом скорректируем по ширине меню
        placement: "bottom",
      }));

      return next;
    });
  }, []);

  // close on outside click
  useEffect(() => {
    if (!openDropdownId) return;

    const onDown = (e) => {
      const menuEl = dropdownMenuRef.current;
      const btnEl = dotsBtnRefs.current.get(openDropdownId);

      const clickedMenu = menuEl && menuEl.contains(e.target);
      const clickedButton = btnEl && btnEl.contains(e.target);

      if (!clickedMenu && !clickedButton) {
        setOpenDropdownId(null);
      }
    };

    window.addEventListener("mousedown", onDown, true);
    return () => window.removeEventListener("mousedown", onDown, true);
  }, [openDropdownId]);

  // close on scroll (simple UX, avoids stale position)
  useEffect(() => {
    if (!openDropdownId) return;
    const onScroll = () => setOpenDropdownId(null);
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [openDropdownId]);

  // flip up/down + clamp to viewport after menu render
  useLayoutEffect(() => {
    if (!openDropdownId) return;

    const btnEl = dotsBtnRefs.current.get(openDropdownId);
    const menuEl = dropdownMenuRef.current;
    if (!btnEl || !menuEl) return;

    const rect = btnEl.getBoundingClientRect();
    const menuRect = menuEl.getBoundingClientRect();

    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const shouldOpenUp = spaceBelow < menuRect.height && spaceAbove > spaceBelow;

    let top = shouldOpenUp
      ? rect.top - menuRect.height - gap
      : rect.bottom + gap;

    // align menu right edge to button right edge
    let left = rect.right - menuRect.width;

    // clamp to viewport
    const minLeft = 8;
    const maxLeft = window.innerWidth - menuRect.width - 8;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    const minTop = 8;
    const maxTop = window.innerHeight - menuRect.height - 8;
    top = Math.max(minTop, Math.min(top, maxTop));

    setDropdownPos({
      top,
      left,
      placement: shouldOpenUp ? "top" : "bottom",
    });
  }, [openDropdownId]);

  // ===== LOAD CATEGORIES (1) =====
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const tk = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/api/product_categories`, {
          headers: { Authorization: `Bearer ${tk}` },
        });
        const result = await res.json();
        if (Array.isArray(result.data)) {
          const options = result.data.map((cat) => ({
            value: cat.id,
            label: cat.title,
          }));
          setCategoriesList(options);
        } else {
          console.error("Неверный формат категорий:", result);
        }
      } catch (error) {
        console.error("Ошибка при загрузке категорий:", error);
      }
    };

    fetchCategories();
  }, []);

  // ===== LOAD STORAGES =====
  useEffect(() => {
    const fetchStorages = async () => {
      try {
        const tk = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/api/storages`, {
          headers: { Authorization: `Bearer ${tk}` },
        });

        const result = await res.json();

        if (Array.isArray(result.data) && result.data.length > 0) {
          const sortedStorages = result.data.sort((a, b) => a.id - b.id);

          const options = sortedStorages.map((storage) => ({
            value: storage.id,
            label: storage.name,
          }));
          setStoragesList(options);

          const firstStorage = options[0];
          setSelectedStorage(firstStorage.value);

          const newFilters = { ...filterParams, storage_id: firstStorage.value };
          setFilterParams(newFilters);

          fetchPageData(1, newFilters);
        }
      } catch (error) {
        console.error("Ошибка при загрузке складов:", error);
      }
    };

    fetchStorages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const tk = localStorage.getItem("token");
      const trimmed = searchValue.trim();

      if (trimmed) {
        const isNtin = /^\d+$/.test(trimmed);
        let url = `${API_URL}/api/products?storage_id=${selectedStorage}`;

        // ✅ если выбран type — добавим в поиск тоже
        if (type) url += `&type=${encodeURIComponent(type)}`;

        url += isNtin
          ? `&ntin=${encodeURIComponent(trimmed)}`
          : `&name_ru=${encodeURIComponent(trimmed)}`;

        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${tk}` },
        });

        const result = await res.json();

        if (Array.isArray(result.data)) {
          setTableData(result.data);
          setTotalItems(result.data.length);
        } else {
          setTableData([]);
          setTotalItems(0);
        }
      } else {
        fetchPageData(currentPage, { ...filterParams, storage_id: selectedStorage });
      }
    } catch (error) {
      console.error("Ошибка при поиске:", error);
    } finally {
      setLoading(false);
    }
  };

  // ===== LOAD CATEGORIES (2) =====
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const result = await authFetch(`${API_URL}/api/product_categories`);
        if (Array.isArray(result.data)) {
          const options = result.data.map((cat) => ({
            value: cat.id,
            label: cat.title,
          }));
          setCategoriesList(options);
        } else {
          console.error("Неверный формат категорий:", result);
        }
      } catch (error) {
        console.error("Ошибка при загрузке категорий:", error);
      }
    };

    if (token) fetchCategories();
  }, [token]);

  const fetchPageData = async (page = 1, filters = filterParams) => {
    try {
      setLoading(true);
      const storageId = filters.storage_id ?? 1;

      let url = `${API_URL}/api/products?page=${page}&limit=${rowsPerPage}`;
      if (storageId) url += `&storage_id=${storageId}`;
      if (filters.category_id) url += `&category_id=${filters.category_id}`;

      // ✅ если выбран type — отправляем ?type=universal и т.д.
      if (filters.type) url += `&type=${encodeURIComponent(filters.type)}`;

      const tk = localStorage.getItem("token");
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${tk}` },
      });

      const result = await res.json();
      console.log("Ответ от сервера:", result);

      if (!Array.isArray(result.data)) {
        throw new Error("Сервер вернул данные в неверном формате");
      }

      setTableData(result.data);
      setTotalItems(result.total || result.data.length);
    } catch (error) {
      console.error("Ошибка при загрузке данных:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchPageData(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, token]);

  const toggleAddMenu = () => {
    setShowAddMenu((prev) => !prev);
    setShowActionsMenu(false);
  };

  const toggleActionsMenu = () => {
    setShowActionsMenu((prev) => !prev);
    setShowAddMenu(false);
  };

  const handleSelectAll = () => {
    const newValue = !selectAll;
    setSelectAll(newValue);
    setSelectedItems(newValue ? tableData.map((item) => item.id) : []);
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (selectedItems.length === tableData.length && tableData.length > 0) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedItems, tableData]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setSelectedItems([]);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        {sections.map((section) => (
          <button
            key={section.id}
            className={`${styles.tabButton} ${
              activeSection === section.id ? styles.active : ""
            }`}
            onClick={() => {
              setActiveSection(section.id);
              if (section.id === "categories") {
                setShowMenuTwo?.(true);
              } else {
                setShowMenuTwo?.(false);
              }
              if (section.id === "nomenclature") {
                fetchPageData(1, filterParams);
              }
            }}
          >
            {section.label}
          </button>
        ))}
      </div>

      {!["arrival", "movement", "categories", "writeoff", "revision", "return"].includes(
        activeSection
      ) && (
        <div className={styles.actions}>
          <div className={styles.dropdownWrapper}>
            <button className={styles.addButton} onClick={toggleAddMenu}>
              Добавить
            </button>
            {showAddMenu && (
              <div className={styles.dropdown}>
                <button
                  onClick={() => {
                    setShowAddMenu(false);
                    setShowAddSingleProduct(true);
                  }}
                >
                  Товар однотипный
                </button>
                <button
                  onClick={() => {
                    setShowAddMenu(false);
                    setShowAddMultiProduct(true);
                  }}
                >
                  Товар многотипный
                </button>
                <button>Услуга</button>
                <button>Составной товар</button>
              </div>
            )}
          </div>

          <div className={styles.dropdownWrapper}>
            <button className={styles.actionButton} onClick={toggleActionsMenu}></button>
            {showActionsMenu && (
              <div className={styles.dropdown}>
                <button>Импорт номенклатуры</button>
                <button>Экспорт номенклатуры</button>
                <button>Выгрузка PLU</button>
                <button>Управление весами</button>
              </div>
            )}
          </div>
        </div>
      )}

      {!["arrival", "movement", "categories", "writeoff", "revision", "return"].includes(
        activeSection
      ) && (
        <div className={styles.filters}>
          <CustomSelect
            className={styles.filters__select}
            options={storagesList}
            value={selectedStorage}
            onChange={(selected) => {
              setSelectedStorage(selected);
              const newFilters = { ...filterParams, storage_id: selected };
              setFilterParams(newFilters);
              fetchPageData(1, newFilters);
            }}
            placeholder="Склады"
          />

          <CustomSelect
            className={styles.filters__select}
            options={PRODUCT_TYPES}
            value={type}
            onChange={(selected) => {
              setType(selected);
              const newFilters = { ...filterParams, type: selected };
              setFilterParams(newFilters);
              fetchPageData(1, newFilters);
            }}
            placeholder="Тип товара"
          />

          <CustomSelect
            className={styles.filters__select}
            options={["Поиск товара 1", "Поиск товара 2"]}
            value={searchItem}
            onChange={setSearchItem}
            placeholder="Поиск товара"
          />

          <CustomSelect
            className={styles.filters__select}
            options={categoriesList}
            value={category}
            onChange={(selected) => {
              console.log("[onChange] Выбрана категория ID:", selected);
              setCategory(selected);
              const newFilters = { ...filterParams, category_id: selected };
              setFilterParams(newFilters);
              fetchPageData(1, newFilters);
            }}
            placeholder="Поиск категории"
          />

          {!showSearch ? (
            <button className={styles.searchButton} onClick={() => setShowSearch(true)}></button>
          ) : (
            <input
              type="text"
              className={`${styles.searchInput} ${styles.searchInputVisible}`}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              placeholder="Введите Штрихкод"
            />
          )}

          <button className={styles.filterButton} onClick={() => setShowFilterSidebar(true)}></button>
          <button
            className={styles.clearButton}
            onClick={() => {
              setSearchValue("");
              setShowSearch(false);

              setType("");
              setCategory("");
              const newFilters = { storage_id: selectedStorage };
              setFilterParams(newFilters);

              fetchPageData(1, newFilters);
            }}
          ></button>
        </div>
      )}

      <div className={styles.content}>
        {activeSection === "nomenclature" && (
          <>
            {loading && <div className={styles.loading}>Загрузка...</div>}

            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>
                      <label className={styles.checkboxContainer}>
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                        <span className={styles.customCheckbox}></span>
                      </label>
                    </th>
                    <th>Название</th>
                    <th>Артикул</th>
                    <th>Категория</th>
                    <th>Штрихкод</th>
                    <th>Количество</th>
                    <th>Цена закупки (₸)</th>
                    <th>Цена продажи (₸)</th>
                    <th>
                      <span className={styles.settingIcon}></span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {tableData.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <label className={styles.checkboxContainer}>
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(item.id)}
                            onChange={() => handleSelectItem(item.id)}
                          />
                          <span className={styles.customCheckbox}></span>
                        </label>
                      </td>

                      <td>{item.name_ru}</td>
                      <td>{item.article || "—"}</td>

                      <td>
                        {categoriesList.find((cat) => cat.value === item.category_id)?.label || "—"}
                      </td>

                      <td>{item.ntin || "—"}</td>

                      <td>
                        {item.quantity != null
                          ? parseFloat(item.quantity).toFixed(3).replace(/\.?0+$/, "")
                          : "—"}
                      </td>

                      <td>{item.purchase_price || "—"}</td>
                      <td>{item.sell_price || "—"}</td>

                      {/* ✅ ВАЖНО: НИКАКИХ <td> внутри <td> */}
                      <td className={styles.tDots}>
                        <button
                          className={styles.optionsButton}
                          ref={(el) => {
                            if (el) dotsBtnRefs.current.set(item.id, el);
                            else dotsBtnRefs.current.delete(item.id);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOptionsClick(item.id);
                          }}
                          type="button"
                        >
                          <span className={styles.dots}>
                            <span className={styles.dot}></span>
                            <span className={styles.dot}></span>
                            <span className={styles.dot}></span>
                          </span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ✅ PORTAL DROPDOWN */}
            {openDropdownId &&
              createPortal(
                <div
                  ref={dropdownMenuRef}
                  className={styles.optionsDropdownPortal}
                  style={{
                    position: "fixed",
                    top: dropdownPos.top,
                    left: dropdownPos.left,
                    zIndex: 9999,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {(() => {
                    const current = tableData.find((x) => x.id === openDropdownId);
                    if (!current) return null;

                    const close = () => setOpenDropdownId(null);

                    return (
                      <>
                        <button
                          className={styles.show}
                          onClick={() => {
                            setProductToView(current);
                            close();
                          }}
                          type="button"
                        >
                          Посмотреть
                        </button>

                        <button
                          className={styles.edit}
                          onClick={() => {
                            setProductToEdit(current);
                            setShowAddSingleProduct(true);
                            close();
                          }}
                          type="button"
                        >
                          Редактировать
                        </button>

                        <button
                          className={styles.print}
                          onClick={() => {
                            setProductToPrint(current);
                            close();
                          }}
                          type="button"
                        >
                          Распечатать штрихкод
                        </button>

                        <button
                          className={styles.delete}
                          onClick={() => {
                            const selectedItem = tableData.find((i) => i.id === current.id);
                            setSingleDeleteItem(selectedItem);
                            setShowDeleteModal(true);
                            close();
                          }}
                          type="button"
                        >
                          Удалить
                        </button>
                      </>
                    );
                  })()}
                </div>,
                document.body
              )}

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.pageButtonSymbol}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &#8249;
                </button>

                {currentPage <= 5 &&
                  [1, 2, 3, 4, 5].map((page) => {
                    if (page > totalPages) return null;
                    if (page === totalPages) return null;
                    const isActive = currentPage === page;
                    return (
                      <button
                        key={page}
                        className={`${styles.pageButton} ${isActive ? styles.activePage : ""}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    );
                  })}

                {currentPage > 5 &&
                  [currentPage - 1, currentPage, currentPage + 1].map((page) => {
                    if (page < 1 || page > totalPages) return null;
                    if (page === totalPages) return null;
                    const isActive = currentPage === page;
                    return (
                      <button
                        key={page}
                        className={`${styles.pageButton} ${isActive ? styles.activePage : ""}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    );
                  })}

                {totalPages > 4 && currentPage < totalPages - 2 && (
                  <span className={styles.ellipsis}>...</span>
                )}

                {totalPages > 5 && (
                  <button
                    className={`${styles.pageButton} ${
                      currentPage === totalPages ? styles.activePage : ""
                    }`}
                    onClick={() => handlePageChange(totalPages)}
                  >
                    {totalPages}
                  </button>
                )}

                <button
                  className={styles.pageButtonSymbol}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  &#8250;
                </button>
              </div>
            )}
          </>
        )}

        {activeSection === "categories" && <CategoriesSection />}
        {activeSection === "arrival" && <ArrivalSection />}
        {activeSection === "movement" && <MovementSection />}
        {activeSection === "writeoff" && <WriteoffSection />}
        {activeSection === "return" && <RefundSection />}
        {activeSection === "revision" && <RevisionSection />}
      </div>

      {showFilterSidebar && (
        <>
          <div
            className={`${styles.overlay} ${showFilterSidebar ? styles.visible : ""}`}
            onClick={() => setShowFilterSidebar(false)}
          />
          <FilterSidebar
            onClose={() => setShowFilterSidebar(false)}
            onApplyFilters={(filters) => {
              setFilterParams(filters);
              fetchPageData(1, filters);
            }}
          />
        </>
      )}

      {selectedItems.length > 0 && (
        <ActionBar
          count={selectedItems.length}
          onDelete={() => setShowDeleteModal(true)}
          onPrint={() => {
            const selectedProduct = tableData.find((item) => item.id === selectedItems[0]);
            if (selectedProduct) setProductToPrint(selectedProduct);
          }}
          onMove={() => setShowMovementModal(true)}
          onExport={() => alert("Экспорт данных")}
          onCategoryAssign={() => setShowCategoryModal(true)}
          onArrival={() => setShowArrivalModal(true)}
        />
      )}

      {showMovementModal && (
        <MovementModal
          onClose={() => setShowMovementModal(false)}
          onSubmit={(data) => {
            console.log("Данные перемещения:", data);
          }}
        />
      )}

      {showCategoryModal && (
        <CategoryModal
          onClose={() => setShowCategoryModal(false)}
          onSave={(selectedCategory) => {
            console.log("Категория выбрана:", selectedCategory);
          }}
        />
      )}

      {productToMove && <MovementModal product={productToMove} onClose={() => setProductToMove(null)} />}

      {productToView && (
        <ProductDrawer
          product={productToView}
          onClose={() => setProductToView(null)}
          onPrint={(p) => setProductToPrint(p)}
          onMove={(p) => setProductToMove(p)}
        />
      )}

      {productToPrint && <BarcodePrintModal product={productToPrint} onClose={() => setProductToPrint(null)} />}

      {showAddMultiProduct && <AddMultiProductDrawer onClose={() => setShowAddMultiProduct(false)} />}

      {showAddSingleProduct && (
        <AddSingleProductDrawer
          onClose={() => {
            setShowAddSingleProduct(false);
            setProductToEdit(null);
          }}
          onProductAdded={() => fetchPageData(currentPage)}
          productToEdit={productToEdit}
        />
      )}

      {showDeleteModal && (
        <CustomDeleteModal
          products={
            singleDeleteItem
              ? [{ id: singleDeleteItem.id, name: singleDeleteItem.name_ru || "Без названия" }]
              : tableData
                  .filter((item) => selectedItems.includes(item.id))
                  .map((item) => ({ id: item.id, name: item.name_ru || "Без названия" }))
          }
          onClose={() => {
            setShowDeleteModal(false);
            setSingleDeleteItem(null);
          }}
          onDeleted={() => {
            fetchPageData(currentPage, filterParams);
            setSelectedItems([]);
            setSingleDeleteItem(null);
          }}
        />
      )}

      {showArrivalModal && (
        <ArrivalModal
          onClose={() => setShowArrivalModal(false)}
          onSelect={(selected) => {
            console.log("Склад выбран:", selected);
            setShowArrivalModal(false);
          }}
        />
      )}
    </div>
  );
};

export default ProductsView;
